import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Minus, Plus, ShoppingBag } from "lucide-react";

export default function Cart() {
  const { items, updateQty, remove, total, clear } = useCart();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        items: items.map((i) => ({ product_id: i.id, quantity: i.qty })),
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  return (
    <div className="px-6 md:px-12 lg:px-16 py-16 min-h-[60vh]" data-testid="cart-page">
      <div className="max-w-5xl mx-auto">
        <div className="text-halo-red font-sports uppercase tracking-[0.3em] text-xs mb-3">Your Cart</div>
        <h1 className="font-sports uppercase tracking-tight text-5xl sm:text-6xl text-halo-navy">
          Almost <span className="text-halo-red">There.</span>
        </h1>

        {items.length === 0 ? (
          <div className="mt-12 border-2 border-dashed border-halo-navy/30 p-16 text-center">
            <ShoppingBag className="w-12 h-12 text-halo-navy/30 mx-auto" strokeWidth={1.5} />
            <p className="font-editorial italic text-xl text-halo-navy/60 mt-4">Your bullpen is empty.</p>
            <Link to="/shop" data-testid="cart-shop-link"
                  className="inline-block mt-6 bg-halo-red hover:bg-halo-redHover text-white px-6 py-3 font-sports uppercase tracking-wider text-sm">
              Browse the Shop
            </Link>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 space-y-4">
              {items.map((item) => (
                <div key={item.key || item.id} data-testid={`cart-item-${item.key || item.id}`}
                     className="flex gap-4 bg-white border border-border p-4">
                  <img src={item.image} alt={item.name} className="w-24 h-24 object-cover bg-halo-cream" />
                  <div className="flex-1">
                    <h3 className="font-sports uppercase tracking-tight text-lg text-halo-navy">{item.name}</h3>
                    {item.size && (
                      <div className="mt-1 inline-block bg-halo-navy text-halo-cream font-sports uppercase tracking-widest text-[10px] px-2 py-0.5">
                        Size {item.size}
                      </div>
                    )}
                    <div className="font-sports text-xl mt-1 text-halo-navy">${item.price.toFixed(2)}</div>
                    <div className="mt-3 flex items-center gap-3">
                      <div className="inline-flex border border-halo-navy">
                        <button data-testid={`qty-minus-${item.key || item.id}`} onClick={() => updateQty(item.key || item.id, item.qty - 1)} className="px-3 py-1 hover:bg-halo-navy hover:text-halo-cream">
                          <Minus className="w-3 h-3" />
                        </button>
                        <span data-testid={`qty-value-${item.key || item.id}`} className="px-4 py-1 font-sports">{item.qty}</span>
                        <button data-testid={`qty-plus-${item.key || item.id}`} onClick={() => updateQty(item.key || item.id, item.qty + 1)} className="px-3 py-1 hover:bg-halo-navy hover:text-halo-cream">
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <button data-testid={`remove-${item.key || item.id}`} onClick={() => remove(item.key || item.id)} className="text-halo-navy/50 hover:text-halo-red">
                        <Trash2 className="w-4 h-4" strokeWidth={2.5} />
                      </button>
                    </div>
                  </div>
                  <div className="font-sports text-xl text-halo-navy">${(item.price * item.qty).toFixed(2)}</div>
                </div>
              ))}
              <button onClick={clear} data-testid="cart-clear-btn"
                      className="text-xs font-sports uppercase tracking-widest text-halo-navy/50 hover:text-halo-red">
                Clear cart
              </button>
            </div>

            <div className="bg-halo-navy text-halo-cream p-7 h-fit">
              <h2 className="font-sports uppercase tracking-tight text-2xl mb-6">Order Summary</h2>
              <div className="space-y-2 font-body text-sm">
                <div className="flex justify-between"><span className="opacity-70">Subtotal</span><span>${total.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="opacity-70">Shipping</span><span>Calculated at checkout</span></div>
              </div>
              <div className="border-t border-halo-navyLight mt-5 pt-5 flex justify-between items-baseline">
                <span className="font-sports uppercase tracking-wider">Total</span>
                <span data-testid="cart-total" className="font-sports text-3xl">${total.toFixed(2)}</span>
              </div>
              <button
                data-testid="checkout-btn"
                onClick={checkout} disabled={loading}
                className="w-full mt-6 bg-halo-red hover:bg-halo-redHover disabled:opacity-50 text-white font-sports uppercase tracking-wider py-4 text-sm"
              >
                {loading ? "Loading..." : "Checkout with Stripe →"}
              </button>
              <p className="text-[10px] font-body opacity-50 mt-3 text-center">Secured by Stripe. Test mode.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
